import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	ViewChild,
	afterEveryRender,
	computed,
	effect,
	input,
	output,
	signal
} from '@angular/core';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { cloneHelper } from 'lib/helpers/clone-helper.class.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { LayoutSize, LayoutStyles } from 'lib/ui/layout/layout-ui.service.js';
import { map } from 'rxjs';
import { CoreAppComponent, IUserMenuItem } from '../../../core/core-app.component.js';
import { IFoodNutritionGraphConfig, foodHelper } from '../../../modules/foods/food.helper.js';
import {
	AuthPageLayoutStyle,
	IPageMetadata,
	IPageTag,
	ISharedData,
	ITypePageCard,
	ITypePageCardAvatarUpload,
	ITypeParentRoute
} from '../../../pages/type-routes/type-route.models.js';
import { SecondaryMenuItem } from '../../layout.models.js';
import { IMenuItemWithState, IParentRouteWithState } from '../../services/layout-service.js';

type EditAvatarFormItem = {
	readonly avatar?: readonly CosmosDb.ICosmosFileReference[];
};

@Component({
	selector: 'layout-auth-page',
	templateUrl: './layout-auth-page.component.html',
	styleUrls: ['./layout-auth-page.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class LayoutAuthPageComponent extends CoreAppComponent {
	public readonly sharedData = input<ISharedData<any>>();
	public readonly menuItems = input<readonly IMenuItemWithState[]>();
	public readonly pageMetadata = input<IPageMetadata>();
	public readonly parentRoutes = input<readonly ITypeParentRoute[]>();
	public readonly layoutSize = input<LayoutSize>();
	public readonly secondaryMenuItems = input.required<readonly SecondaryMenuItem[]>();

	public readonly sharedDataUpdated = output<ISharedData<any>>();

	public readonly latestAvatarFile = signal<CosmosDb.ICosmosFileReference | undefined>(undefined);
	public readonly showTagIcon: boolean = true;

	public readonly mobileUserMenuItemsWidth = computed<string>(() => {
		return super.calculateFlexWidth(2, 16);
	});

	public readonly parentRouteWrapLength = computed<number>(() => {
		return this.isMobileView() ? 20 : 30;
	});

	public readonly pageTitle = computed<string>(() => {
		return this.pageMetadata()?.title ?? '';
	});

	public readonly formattedUpdatedTime = computed<string | undefined>(() => {
		const updated = this.pageCard()?.updated;

		if (!updated) {
			return undefined;
		}

		return super.fromNow(updated);
	});

	public readonly pageTags = computed<IPageTag[] | undefined>(() => {
		const card = this.pageMetadata()?.card;

		if (!card) {
			return undefined;
		}

		const tags: IPageTag[] = [...(card.tags ?? [])];

		if (!tags.length) {
			return undefined;
		}

		return tags;
	});

	public readonly showCard = computed<boolean>(() => {
		const pageMetadata = this.pageMetadata();

		if (!pageMetadata) {
			return false;
		}

		if (pageMetadata.card && pageMetadata.card && (pageMetadata.card.tags || pageMetadata.card.nutrition)) {
			return true;
		}
		return false;
	});

	public readonly layoutSizeStyles = computed<LayoutStyles>(() => {
		return this.dependencies.layoutUiService.getLayoutSizeStyles({
			screenService: this.dependencies.screenService,
			type: this.layoutSize() ?? 'large'
		});
	});

	public readonly showMenuSelector = computed<boolean>(() => {
		if (this.isTrialExpired()) {
			return false;
		}

		const menuItems = this.menuItems()?.length ?? 0;
		if (menuItems > 1) {
			return true;
		}
		return false;
	});

	public readonly authPageLayoutStyle = signal<AuthPageLayoutStyle>('default');
	public readonly nutritionGraphConfig = computed<IFoodNutritionGraphConfig | undefined>(() => {
		const pageMetadata = this.pageMetadata();
		if (!pageMetadata?.card?.nutrition) {
			return undefined;
		}

		return foodHelper.getNutritionGraphConfig(pageMetadata.card.nutrition, { per100g: false });
	});

	public readonly calculatedMetadataCardButtons = computed<readonly IButton[]>(() => {
		return this.pageMetadata()?.card?.buttons ?? [];
	});

	public readonly calculatedParentRoutes = computed<readonly IParentRouteWithState[]>(() => {
		return this.dependencies.layoutService.getParentRoutesWithState(this.parentRoutes() ?? []);
	});

	@ViewChild('menuItemSelectorTrigger', { static: false }) selectionTriggerElement?: ElementRef<HTMLElement>;

	protected readonly showSecondaryMenu = computed<boolean>(() => {
		return (this.secondaryMenuItems()?.length ?? 0) > 1 ? true : false;
	});

	public readonly widthOfMenuItemSelectorTrigger = signal<number>(300);
	public readonly menuItemSelectorWrapSize = computed<number>(() => {
		const menuItemsCount = this.menuItems()?.length ?? 0;

		if (menuItemsCount >= 6) {
			return 2;
		}

		return 1;
	});

	public readonly pageCard = computed<ITypePageCard | undefined>(() => {
		return this.pageMetadata()?.card;
	});

	public readonly showCardOnMobile = computed<boolean>(() => {
		const card = this.pageCard();

		if (card) {
			return true;
		}

		return false;
	});
	public readonly showCardOnDesktop = computed<boolean>(() => {
		const card = this.pageCard();

		if (card) {
			return true;
		}

		return false;
	});

	constructor() {
		super();

		effect(() => {
			const pageMetadata = this.pageMetadata();

			// store avatar file for future reference
			this.latestAvatarFile.set(pageMetadata?.card?.avatar?.upload?.avatarFile);
		});

		afterEveryRender(() => {
			const triggerElement = this.selectionTriggerElement;
			if (triggerElement) {
				this.widthOfMenuItemSelectorTrigger.set(triggerElement.nativeElement.offsetWidth);
			}
		});
	}

	handleEditTitle(title: string): void {
		const titleConfig = this.pageMetadata()?.card?.title;

		if (!titleConfig?.save) {
			return;
		}

		super.subscribeToObservable(
			titleConfig.save(title).pipe(
				map(() => {
					super.notify('Title has been updated');
					const sharedData = this.sharedData();

					if (sharedData && titleConfig.updateSharedData) {
						const updatedSharedData = titleConfig.updateSharedData(title, sharedData);
						this.sharedDataUpdated.emit(cloneHelper.clone(updatedSharedData));
					}
				})
			)
		);
	}

	handleEditAvatar(): void {
		const avatarUpload = this.pageMetadata()?.card?.avatar?.upload;

		if (!avatarUpload) {
			return;
		}

		const latestAvatarFile = this.latestAvatarFile();

		this.dependencies.dialogService.openFormDialog<EditAvatarFormItem>({
			size: 'default',
			title: 'Edit image',
			fields: [
				{
					title: 'Avatar',
					type: 'image',
					propertyName: 'avatar',
					required: false,
					fileRemove: (file) => {
						// save on remove
						this.saveAvatar(avatarUpload, undefined);
					},
					fileSelected: (file) => {
						// save file
						this.saveAvatar(avatarUpload, file);
					},
					fileUpload: (file) =>
						avatarUpload.fileUpload(file).pipe(
							map((response) => {
								// save on upload
								this.saveAvatar(avatarUpload, response);

								return [response];
							})
						),
					imageCrop: avatarUpload.imageCrop,
					enableImageGallery: true
				}
			],
			item: {
				avatar: latestAvatarFile ? [latestAvatarFile] : []
			},
			hideSaveButton: true,
			save: (item) => {
				const file = item.avatar?.length ? item.avatar[0] : undefined;
				this.saveAvatar(avatarUpload, file);
			}
		});
	}

	private saveAvatar(
		avatarUpload: ITypePageCardAvatarUpload<any>,
		file: CosmosDb.ICosmosFileReference | undefined
	): void {
		this.latestAvatarFile.set(file);

		super.subscribeToObservable(
			avatarUpload.save(file).pipe(
				map((response) => {
					super.notifyUploaded();

					const sharedData = this.sharedData();

					if (sharedData) {
						const updatedSharedData = avatarUpload.updateSharedData(file, sharedData);
						this.sharedDataUpdated.emit(cloneHelper.clone(updatedSharedData));
					}
				})
			)
		);
	}

	isUserMenuItemActive(menuItem: IUserMenuItem): boolean {
		if (menuItem.routerLink?.toLowerCase() === this.dependencies.router.url.toLowerCase()) {
			return true;
		}
		return false;
	}
}
